import React, { useState } from 'react'
import { Button } from 'antd'
import { FilterOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ArgumentModal from './ArgumentModal'
import { currentUserSortedCompaniesSelector } from '../../../../../redux/session/currentUser/selectors'
import { CustomReportArgument } from '../../../../../redux/context/customReports/types'
import { setCustomReportArguments } from '../../../../../redux/context/customReports/actions'
import { customReportArgumentsSelector } from '../../../../../redux/context/customReports/selectors'
import { useCtxUpdateCustomReport } from '../../../../../features/customReport/queries/report/useCtxUpdateCustomReport'
import customReportQueryKeys from '../../../../../features/customReport/queries/customReportQueryKeys'
import { showErrorMessage } from '../../../../../utils/message'

interface ArgumentsDropdownProps {
  defaultArguments?: CustomReportArgument[]
  reportId?: number
}

const ArgumentsDropdown: React.FC<ArgumentsDropdownProps> = ({ defaultArguments = [], reportId }) => {
  const { t } = useTranslation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { mutateAsync: updateCustomReport, isPending } = useCtxUpdateCustomReport()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const stateArguments = useSelector(customReportArgumentsSelector)

  const companies = useSelector(currentUserSortedCompaniesSelector)

  const activeArguments = stateArguments && stateArguments.length > 0 ? stateArguments : defaultArguments

  const handleSaveDefaults = async (updatedArgs: CustomReportArgument[]) => {
    if (!reportId) return

    try {
      const mergedArguments = defaultArguments.map(arg => {
        const updated = updatedArgs.find(u => u.id === arg.id)
        return updated ?? arg
      })

      await updateCustomReport({
        data: { id: reportId, args: mergedArguments }
      })

      setIsModalVisible(false)
      dispatch(setCustomReportArguments([]))
      queryClient.invalidateQueries({
        queryKey: [{ ...customReportQueryKeys.sections()[0], reportId }]
      })
    } catch (error) {
      showErrorMessage()
    }
  }

  const handleUse = (updatedArgs: CustomReportArgument[]) => {
    dispatch(setCustomReportArguments(updatedArgs))
    setIsModalVisible(false)
  }

  return (
    <div>
      <Button
        data-cy="arguments-button"
        disabled={!reportId || defaultArguments.length === 0}
        icon={<FilterOutlined />}
        onClick={() => setIsModalVisible(true)}
      >
        {t('global:arguments')}
      </Button>

      <ArgumentModal
        visible={isModalVisible}
        arguments={activeArguments}
        defaultArguments={defaultArguments}
        loading={isPending}
        companies={companies}
        onCancel={() => setIsModalVisible(false)}
        onSave={handleSaveDefaults}
        onUse={handleUse}
      />
    </div>
  )
}

export default ArgumentsDropdown
